import type { PayloadAction } from "@reduxjs/toolkit";
import { commonConstants } from "@dotpe/rista-pos-common";

/**
 * Shared parts of LicenceExpirySlice — plan §5.2. Both reducers are identical; the ONLY
 * difference is that web registers the logout `extraReducers` and mobile does not.
 *
 * ⚠️ **First core module to depend on an EXTERNAL package.** `initialState` is not a literal
 * in either app — it is `commonConstants.initialLicenceExpiryState` from
 * `@dotpe/rista-pos-common` (which is also where the `LicenceExpiryNotificationTypes` type
 * used below comes from). That is why `uplift-analyse` reports
 * `initialState UNRESOLVED` for this module on both sides: there is nothing to compare, and
 * that is correct rather than a gap.
 *
 * `@dotpe/rista-pos-common` is declared a PEER dependency of `@rista/core` — both apps pin
 * it (4.0.1) and provide it. Verified to resolve from this directory under both bundlers:
 * webpack takes `dist/esm/index.js`, Metro takes `dist/index.js`, matching what each app
 * already resolves (see CLAUDE.md on the dual-format package and the single RTK instance).
 *
 * ⚠️ `action` must be `PayloadAction<any>`, never `any` — see the ledger.
 */
export interface LicenceExpiryState {
  bannerStyle: {
    textColor: string;
    bgColor: string;
    iconName: ''| commonConstants.LicenceExpiryNotificationTypes;
  }
  bannerText: string;
  showBanner: boolean;
  isCancellable: boolean;
}

/**
 * The initial state itself lives in `@dotpe/rista-pos-common` — re-exported here so both
 * platform files reference ONE expression, exactly as each app referenced one import.
 */
export const sharedInitialState = commonConstants.initialLicenceExpiryState;

/**
 * ⚠️ The reducers are typed against `typeof sharedInitialState`, NOT against
 * `LicenceExpiryState`. That mirrors the apps exactly: there, `LicenceExpiryState` is declared
 * and exported but never used to annotate `initialState`, so RTK contextually types the
 * reducers from the imported constant. The two are NOT the same type — the constant widens
 * `bannerStyle.iconName` to `string`, while the interface narrows it to
 * `'' | LicenceExpiryNotificationTypes` — and annotating with the interface fails to compile
 * under `strict` (`Type 'string' is not assignable to '"" | LicenceExpiryNotificationTypes'`).
 */
type SliceState = typeof sharedInitialState;

/** Identical in both apps. */
export const sharedReducers = {
  getLicenceExpiryStatus: (state: SliceState) => { },
  setLicenceExpiryStatus: (state: SliceState, action: PayloadAction<any>) => {
    return { ...action.payload };
  },
};
